/**
 * Admin Waitlist — list view of every sign-up.
 *
 * Filterable by source (landing-page vs pricing-titanium-waitlist) and tier
 * (titanium-only). Includes a one-click "copy all emails" button so launch
 * day, the owner can paste the list into Resend / Mailchimp / wherever.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Download, Inbox, Trash2 } from 'lucide-react';

import { Button } from '../components/ui/button';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_OPTIONS = [
  { key: '', label: 'All sources' },
  { key: 'landing-page', label: 'Coming-Soon landing' },
  { key: 'pricing-titanium-waitlist', label: 'Pricing — Titanium' },
];

const TIER_OPTIONS = [
  { key: '', label: 'All tiers' },
  { key: 'titanium', label: 'Titanium only' },
  { key: 'damascus', label: 'Damascus' },
  { key: 'pro', label: 'Steel' },
];

const AdminWaitlist = () => {
  const [data, setData] = useState(null);
  const [source, setSource] = useState('');
  const [tier, setTier] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source) params.set('source', source);
      if (tier) params.set('tier', tier);
      const r = await axios.get(`${API}/waitlist/admin/list?${params}`, { withCredentials: true });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load waitlist.');
    } finally {
      setLoading(false);
    }
  }, [source, tier]);

  useEffect(() => { load(); }, [load]);

  const deleteEntry = async (email) => {
    if (!window.confirm(`Remove ${email} from the waitlist? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/waitlist/admin/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`${email} removed.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove entry.');
    }
  };

  const copyAllEmails = async () => {
    if (!data?.entries?.length) return;
    const text = data.entries.map((e) => e.email).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${data.entries.length} emails copied to clipboard.`);
    } catch {
      toast.error('Clipboard not available. Use Export CSV instead.');
    }
  };

  const exportCsv = () => {
    if (!data?.entries?.length) return;
    const header = 'email,tier,source,created_at,resend_synced\n';
    const rows = data.entries.map((e) => {
      const cells = [
        e.email,
        e.tier || '',
        e.source || '',
        e.created_at || '',
        e.resend_synced ? 'yes' : 'no',
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
    }).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forge-ledger-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-waitlist-page">
      <PageTitle
        title="Waitlist"
        subtitle="Everyone who's signed up to be notified at launch."
        className="mb-6"
      />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-[#64748B] mb-1">Source</label>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm"
            data-testid="admin-waitlist-source-filter"
          >
            {SOURCE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-[#64748B] mb-1">Tier</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm"
            data-testid="admin-waitlist-tier-filter"
          >
            {TIER_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button
            type="button"
            onClick={copyAllEmails}
            disabled={!data?.entries?.length}
            className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
            data-testid="admin-waitlist-copy-button"
          >
            <Copy className="w-4 h-4 mr-1.5" /> Copy emails
          </Button>
          <Button
            type="button"
            onClick={exportCsv}
            disabled={!data?.entries?.length}
            className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
            data-testid="admin-waitlist-export-button"
          >
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[#64748B]" data-testid="admin-waitlist-loading">Loading…</div>
        ) : !data?.entries?.length ? (
          <div className="p-12 text-center" data-testid="admin-waitlist-empty">
            <Inbox className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
            <p className="text-sm text-[#64748B]">No sign-ups matching this filter.</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center justify-between text-xs">
              <span className="font-semibold text-[#020617]" data-testid="admin-waitlist-count">
                Showing {data.entries.length} of {data.total_matching}
              </span>
              <span className="text-[#94A3B8]">Newest first</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider font-bold text-[#64748B]">
                <tr>
                  <th className="text-left px-5 py-2.5">Email</th>
                  <th className="text-left px-5 py-2.5">Tier</th>
                  <th className="text-left px-5 py-2.5">Source</th>
                  <th className="text-left px-5 py-2.5">Joined</th>
                  <th className="text-left px-5 py-2.5">Resend</th>
                  <th className="px-5 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr
                    key={`${e.email}-${i}`}
                    className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                    data-testid={`admin-waitlist-row-${i}`}
                  >
                    <td className="px-5 py-2.5 font-medium text-[#020617]">{e.email}</td>
                    <td className="px-5 py-2.5 text-[#475569] capitalize">{e.tier || '—'}</td>
                    <td className="px-5 py-2.5 text-[#475569] text-xs">{e.source || '—'}</td>
                    <td className="px-5 py-2.5 text-[#475569] text-xs">
                      {e.created_at ? new Date(e.created_at).toLocaleString('en-GB') : '—'}
                    </td>
                    <td className="px-5 py-2.5 text-xs">
                      {e.resend_synced
                        ? <span className="text-[#047857]">Synced</span>
                        : <span className="text-[#94A3B8]">Local only</span>}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => deleteEntry(e.email)}
                        className="text-[#94A3B8] hover:text-[#DC2626] transition-colors"
                        title={`Remove ${e.email}`}
                        data-testid={`admin-waitlist-delete-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminWaitlist;
